// ~/utils/error-tracking.tsx
import * as Sentry from '@sentry/browser';

/**
 * Creates an error boundary for export operations that handles both expected and unexpected errors
 */
export const trackExportError = (
    error: any,
    context: {
        operation: string;
        exportType: string;
        fileFormat: string;
        fileName?: string;
        data?: any;
    }
) => {
    // Extract operation details
    const { operation, exportType, fileFormat, fileName, data } = context;

    // Get error details
    const errorMessage = error instanceof Error ? error.message : String(error);
    const errorName = error instanceof Error ? error.name : 'Unknown Error';
    const errorStack = error instanceof Error ? error.stack : undefined;

    // Check for specific error types to provide better context
    let errorCategory = 'unknown';
    let userFacingMessage = 'An error occurred during export';

    // Network errors
    if (
        errorMessage.includes('network') ||
        errorMessage.includes('internet') ||
        errorName === 'NetworkError' ||
        errorMessage.includes('fetch')
    ) {
        errorCategory = 'network';
        userFacingMessage = 'Network error during export. Please check your internet connection.';
    }

    // File access errors
    else if (
        errorMessage.includes('permission') ||
        errorMessage.includes('access') ||
        errorMessage.includes('denied')
    ) {
        errorCategory = 'permission';
        userFacingMessage = 'Permission denied while trying to save the export file.';
    }

    // Data-related errors
    else if (
        errorMessage.includes('data') ||
        errorMessage.includes('undefined') ||
        errorMessage.includes('null') ||
        errorMessage.includes('not found')
    ) {
        errorCategory = 'data';
        userFacingMessage = 'Error processing data for export. Some data may be missing or invalid.';
    }

    // Log to Sentry with detailed categorization
    Sentry.captureException(error, {
        tags: {
            operation,
            exportType,
            fileFormat,
            errorCategory,
            status: 'error'
        },
        extra: {
            fileName,
            errorDetails: {
                name: errorName,
                message: errorMessage,
                stack: errorStack
            },
            userFacingMessage,
            // Include data size but not the actual data
            dataInfo: data ? {
                type: typeof data,
                isArray: Array.isArray(data),
                length: Array.isArray(data) ? data.length : undefined,
                keys: typeof data === 'object' && data !== null ? Object.keys(data) : undefined
            } : 'No data'
        }
    });

    // Return user-facing details that can be shown in the UI
    return {
        category: errorCategory,
        message: userFacingMessage,
        originalError: errorMessage
    };
};

// Enhanced logging for html2canvas errors
export const trackHtml2CanvasError = (error: any, element: HTMLElement | null, options: any) => {
    try {
        // Element properties that might be useful for debugging
        const elementInfo = element ? {
            tagName: element.tagName,
            id: element.id,
            className: element.className,
            dimensions: {
                offsetWidth: element.offsetWidth,
                offsetHeight: element.offsetHeight,
                clientWidth: element.clientWidth,
                clientHeight: element.clientHeight,
                scrollWidth: element.scrollWidth,
                scrollHeight: element.scrollHeight
            },
            visible: !!(
                element.offsetWidth ||
                element.offsetHeight ||
                element.getClientRects().length
            ),
            style: {
                display: window.getComputedStyle(element).display,
                visibility: window.getComputedStyle(element).visibility,
                position: window.getComputedStyle(element).position,
                overflow: window.getComputedStyle(element).overflow,
                zIndex: window.getComputedStyle(element).zIndex
            },
            childrenCount: element.children.length
        } : 'Element is null';

        // Check for specific html2canvas errors
        let errorType = 'unknown';
        if (error.message?.includes('SecurityError')) {
            errorType = 'cors';
        } else if (error.message?.includes('tainted')) {
            errorType = 'tainted-canvas';
        } else if (element === null) {
            errorType = 'null-element';
        } else if (!element.offsetWidth || !element.offsetHeight) {
            errorType = 'zero-size-element';
        }

        // Log to Sentry with html2canvas specific context
        Sentry.captureException(error, {
            tags: {
                operation: 'html2canvas',
                errorType
            },
            extra: {
                elementInfo,
                options,
                browserInfo: {
                    userAgent: navigator.userAgent,
                    screen: {
                        width: window.screen.width,
                        height: window.screen.height,
                        colorDepth: window.screen.colorDepth
                    },
                    viewport: {
                        width: window.innerWidth,
                        height: window.innerHeight
                    },
                }
            }
        });

        // Return helpful context about the error
        return {
            errorType,
            message: `HTML2Canvas error: ${error.message}`,
            elementStatus: element ? `Element has dimensions ${element.offsetWidth}x${element.offsetHeight}` : 'Element is null'
        };
    } catch (loggingError) {
        // In case our error logging itself fails, capture that too
        Sentry.captureException(loggingError, {
            tags: {
                operation: 'error-logging',
                status: 'meta-error'
            },
            extra: {
                originalError: error.message
            }
        });
        return { errorType: 'logging-error', message: 'Error while logging error details' };
    }
};